// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadSystemList from "@adl/App/Handlers/SystemList/SystemListHandler";
import { SystemListModel } from "@adl/App/Services/SystemListService";
import { CustomAppState } from "@adl/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import submitContactForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SubmitContactForm";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapDispatchToProps = {
    submitContactForm,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    loadSystemList,
};

const mapStateToProps = (state: CustomAppState) => ({
    session: getSession(state),
    choosenTopic: state.components.contactUsForm.fieldValues["topic"],
    topics: state.xSystemList["Topics"],
});

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ContactFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    // xng-changes
    infoGridItem?: GridItemProps;
    infoContiner?: InjectableCss;
    infoLabel?: TypographyPresentationProps;
    infoLink?: LinkPresentationProps;
    infoValue?: TypographyPresentationProps;
}

export const contactFormStyles: ContactFormStyles = {
    formGridItem: {
        width: 12,
        css: css`
            #contactForm {
                width: 100%;
                #reCaptcha {
                    margin-top: 15px;
                }
            }
        `,
    },
    submitButton: {
        css: css`
            float: right;
            margin-top: 15px;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    infoGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    infoContiner: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            margin-bottom: 12px;
        `,
    },
    infoLabel: {
        weight: "bold",
        css: css`
            margin-right: 8px;
        `,
    },
};

const styles = contactFormStyles;
const emailsRegexp = new RegExp(
    "^[\\W]*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4}[\\W]*,{1}[\\W]*)*([\\w+\\-.%]+@[\\w\\-.]+\\.[A-Za-z]{2,4})[\\W]*$",
);

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const ContactFormContext = React.createContext<ContextType>({
    validators: {},
});

const ContactForm = ({
    id,
    fields,
    choosenTopic,
    topics,
    submitContactForm,
    clearForm,
    validateReCaptcha,
    session,
    loadSystemList,
}: Props) => {
    const { submitButtonText, successMessage } = fields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    let emailRecipients = "";
    let salesPersonInfo;

    if (session?.isAuthenticated) {
        salesPersonInfo = JSON.parse(session?.properties?.salesPersonInfo || "{}");
        emailRecipients = `${session?.properties?.branchEmail || ""}${
            salesPersonInfo["Email"] ? `,${salesPersonInfo["Email"]}` : ""
        }`;
    }

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "ContactUs", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        submitContactForm({
            emailRecipients,
            onSuccess: () => {
                setIsModalOpen(true);
                setIsSubmitting(false);
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
        clearForm();
    };

    React.useEffect(() => {
        loadSystemList({
            name: "Topics",
        });
    }, []);

    React.useEffect(() => {
        if (!session.isAuthenticated) {
            topics?.values.map((topic: SystemListModel) => {
                if (topic.name === choosenTopic) {
                    emailRecipients = topic?.description || "dev@xngage.com"; // default email that can be a site msg
                }
                return null;
            });
        }
    }, [choosenTopic]);
    const { contactInfoForDefaultWarehouse, branchEmail, branchPhone } = session.properties;
    const showBranchInfo = contactInfoForDefaultWarehouse || branchEmail || branchPhone;
    const showSalesPersonInfo = contactInfoForDefaultWarehouse || branchEmail || branchPhone;
    return (
        <GridContainer {...styles.container}>
            {showBranchInfo && (
                <GridItem {...styles.infoGridItem}>
                    <Typography variant="h4">{translate("Branch Contact Info")}:</Typography>
                    {contactInfoForDefaultWarehouse && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Manager")}:</Typography>
                            <Typography {...styles.infoValue}>{contactInfoForDefaultWarehouse}</Typography>
                        </StyledWrapper>
                    )}
                    {branchEmail && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Email")}:</Typography>
                            <Link href={`mailto:${branchEmail}`} {...styles.infoLink}>
                                {branchEmail}
                            </Link>
                        </StyledWrapper>
                    )}
                    {branchPhone && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Phone Number")}:</Typography>
                            <Link href={`tel:${branchPhone}`} {...styles.infoLink}>
                                {branchPhone}
                            </Link>
                        </StyledWrapper>
                    )}
                </GridItem>
            )}
            {showSalesPersonInfo && (
                <GridItem {...styles.infoGridItem}>
                    <Typography variant="h4">{translate("Sales Person Contact Info")}:</Typography>
                    {salesPersonInfo["Name"] && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Name")}:</Typography>
                            <Typography {...styles.infoValue}>{salesPersonInfo["Name"]}</Typography>
                        </StyledWrapper>
                    )}
                    {salesPersonInfo["Email"] && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Email")}:</Typography>
                            <Link href={`mailto:${salesPersonInfo["Email"]}`} {...styles.infoLink}>
                                {salesPersonInfo["Email"]}
                            </Link>
                        </StyledWrapper>
                    )}
                    {salesPersonInfo["Phone1"] && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Phone Number")}:</Typography>
                            <Link href={`tel:${salesPersonInfo["Phone1"]}`} {...styles.infoLink}>
                                {salesPersonInfo["Phone1"]}
                            </Link>
                        </StyledWrapper>
                    )}
                    {salesPersonInfo["Phone2"] && (
                        <StyledWrapper {...styles.infoContiner}>
                            <Typography {...styles.infoLabel}>{translate("Additional Phone Number")}:</Typography>
                            <Link href={`tel:${salesPersonInfo["Phone2"]}`} {...styles.infoLink}>
                                {salesPersonInfo["Phone2"]}
                            </Link>
                        </StyledWrapper>
                    )}
                </GridItem>
            )}

            <GridItem {...styles.formGridItem}>
                <ContactFormContext.Provider value={{ validators }}>
                    <form id="contactForm" onSubmit={handleFormSubmit} noValidate>
                        <Zone contentId={id} zoneName="Content" />
                        <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                        <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                            {submitButtonText}
                        </Button>
                        <Modal
                            headline={translate("Message Sent")}
                            contentLabel={successMessage}
                            {...styles.successModal}
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                        >
                            <GridContainer {...styles.modalContainer}>
                                <GridItem {...styles.messageTextGridItem}>
                                    <Typography {...styles.messageText}>{successMessage}</Typography>
                                </GridItem>
                                <GridItem {...styles.buttonGridItem}>
                                    <Button {...styles.continueButton} onClick={modalCloseHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                    </form>
                </ContactFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ContactForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
